import React from 'react';
import { Card, CardContent, Typography, TextField, MenuItem, Box, Alert } from '@mui/material';
import Grid from '@mui/material/Grid';
import type { VaccinationData } from '@app-types/visit';
import { Vaccines } from '@mui/icons-material';

interface VaccinationCardProps {
  vaccination: VaccinationData | null;
  onChange: (vaccination: VaccinationData | null) => void;
  disabled?: boolean;
  isRequired?: boolean;
}

const VaccinationCard: React.FC<VaccinationCardProps> = React.memo(({ 
  vaccination, 
  onChange, 
  disabled = false,
  isRequired = false 
}) => {
  const updateField = <K extends keyof VaccinationData>(field: K, value: VaccinationData[K]) => {
    onChange({ ...(vaccination || {} as VaccinationData), [field]: value });
  };

  if (!isRequired && !vaccination) {
    return null;
  }

  const vacc = vaccination || {} as VaccinationData;

  return (
    <Card sx={{ mb: 2, border: isRequired ? '2px solid' : undefined, borderColor: isRequired ? 'warning.main' : undefined }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Vaccines sx={{ mr: 1, color: 'warning.main' }} />
          <Typography variant="h6">
            Vaccinare
            {isRequired && <Typography component="span" color="error" sx={{ ml: 1 }}>*</Typography>}
          </Typography>
        </Box>
        
        {isRequired && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Pentru vizită de vaccinare, toate câmpurile marcate cu * sunt obligatorii pentru conformitate legală.
          </Alert>
        )}
        
<Grid container spacing={2}>
<Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              required={isRequired}
              label="Produs vaccin"
              value={vacc.product ?? ''}
              onChange={(e) => updateField('product', e.target.value)}
              disabled={disabled}
              placeholder="ex: Nobivac DHPPi"
              error={isRequired && !vacc.product}
            />
          </Grid>
          
<Grid size={{ xs: 6, md: 3 }}>
            <TextField
              fullWidth
              label="Doza"
              value={vacc.dose ?? ''}
              onChange={(e) => updateField('dose', e.target.value)}
              disabled={disabled}
              placeholder="ex: 1ml"
            />
          </Grid>
          
<Grid size={{ xs: 6, md: 3 }}>
            <TextField
              fullWidth
              label="Loc admin."
              value={vacc.site ?? ''}
              onChange={(e) => updateField('site', e.target.value)}
              disabled={disabled}
              placeholder="ex: SC, IM"
            />
          </Grid>
          
<Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="Producător"
              value={vacc.producer ?? ''}
              onChange={(e) => updateField('producer', e.target.value)}
              disabled={disabled}
              placeholder="ex: MSD Animal Health"
            />
          </Grid>
          
<Grid size={{ xs: 12, md: 3 }}>
            <TextField
              fullWidth
              required={isRequired}
              label="Serie/Lot"
              value={vacc.lot ?? ''}
              onChange={(e) => updateField('lot', e.target.value)}
              disabled={disabled}
              error={isRequired && !vacc.lot}
              helperText={isRequired ? "Obligatoriu legal" : ""}
            />
          </Grid>
          
<Grid size={{ xs: 12, md: 3 }}>
            <TextField
              fullWidth
              required={isRequired}
              type="date"
              label="Expiră la"
              InputLabelProps={{ shrink: true }}
              value={vacc.exp ?? ''}
              onChange={(e) => updateField('exp', e.target.value)}
              disabled={disabled}
              error={isRequired && !vacc.exp}
              helperText={isRequired ? "Obligatoriu legal" : ""}
            />
          </Grid>
          
<Grid size={{ xs: 12, md: 6 }}>
            <TextField
              select
              fullWidth
              label="Schematizare"
              value={vacc.scheme ?? ''}
              onChange={(e) => updateField('scheme', e.target.value as VaccinationData['scheme'])}
              disabled={disabled}
            >
              <MenuItem value="">—</MenuItem>
              <MenuItem value="primar">Primar</MenuItem>
              <MenuItem value="rapel">Rapel</MenuItem>
              <MenuItem value="booster">Booster</MenuItem>
            </TextField>
          </Grid>
          
<Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              type="date"
              label="Următorul rapel"
              InputLabelProps={{ shrink: true }}
              value={vacc.nextDue ?? ''}
              onChange={(e) => updateField('nextDue', e.target.value)}
              disabled={disabled}
            />
          </Grid>
          
<Grid size={12}>
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Reacții adverse"
              value={vacc.adverse ?? ''}
              onChange={(e) => updateField('adverse', e.target.value)}
              disabled={disabled}
              placeholder="Notează orice reacție adversă observată"
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
});

VaccinationCard.displayName = 'VaccinationCard';

export default VaccinationCard;
